import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Container, Paper, Typography, List, ListItem, ListItemText, IconButton, Divider, Menu, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DentistModal from '../dentistModal';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import SuccessMessage from "../../components/successMessage";
import ErrorMessage from "../../components/errorMessage";
import Loading from '../../components/loading';


function DentistList() {
    const navigate = useNavigate();

    const [dentists, setDentists] = useState([]);
    const [isLoading, setLoading] = useState(true);

    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedDentist, setSelectedDentist] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedDentistId, setSelectedDentistId] = useState(null);
    const isMenuOpen = Boolean(anchorEl);

    const getDentists = useCallback(() => {
        setLoading(true);
        api.get('/dentista', {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        })
            .then(returnFromApi => {
                setDentists(returnFromApi.data);
                setLoading(false);
            })
                    
            .catch((returnFromApi) => {
                setLoading(false);
                console.error("ops! ocorreu um erro" + returnFromApi);
                setErrorMessage(returnFromApi.response.data);
                if (returnFromApi.status === 401) {
                    navigate("/expired-session");
                }        
            });
    }, [navigate]);

    useEffect(() => {
        getDentists();
    }, [getDentists]);

    const clickMenu = (event, dentistId) => {
        setAnchorEl(event.currentTarget);
        setSelectedDentistId(dentistId);
    };

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const editDentist = () => {
        const dentistToEdit = dentists.find(d => d.id === selectedDentistId);
        if (dentistToEdit) {
            setSelectedDentist(dentistToEdit);
            setModalOpen(true);
        }
        closeMenu();
    };

    const openConfirmModal = () => {
        setAnchorEl(null);
        setConfirmModalOpen(true);
    };

    const confirmDelete = () => {
        setLoading(true);
        setConfirmModalOpen(false);
        api.delete(`/dentista/${selectedDentistId}`, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then(returnFromApi => {
            setDentists(dentists.filter(dentist => dentist.id !== selectedDentistId));

            setSuccessMessage(returnFromApi.data);
            setShowSuccessMessage(true);
        }).catch(returnFromApi => {
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi.response.data);
            console.error("Erro ao excluir o dentista!", returnFromApi);
            if (returnFromApi.response?.status === 401) {
                navigate('/expired-session');
            }
        }).finally(() => {
            setSelectedDentistId(null);
            setLoading(false);
        });
    };

    const closeConfirmModal = () => {
        setConfirmModalOpen(false);
        setSelectedDentistId(null);
    };

    const openCreateModal = () => {
        setSelectedDentist(null);
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
        setSelectedDentist(null);
    };

    function saveDentist(returnFromApi) {
        getDentists();
        closeModal();
        if (returnFromApi.data) {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
        } else {
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi.response.data);
        }
    };

    return (
        <>
            <Loading isToHidden={isLoading === false} />
            {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
            {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

            <Box sx={{ p: 4 }}>
                <Container maxWidth="lg">
                    <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{ p: 4, borderRadius: '12px' }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: '600' }}>
                                Dentistas
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={openCreateModal}
                            >
                                Adicionar Dentista
                            </Button>
                        </Box>

                        <List>
                            {dentists.map((dentist) => (
                                <React.Fragment key={dentist.id}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton
                                                onClick={(event) => clickMenu(event, dentist.id)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={dentist.nome} />
                                    </ListItem>
                                    <Divider component="li" />
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>
                </Container>
                <Menu
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={closeMenu}
                >
                    <MenuItem onClick={editDentist}>
                        Editar Dentista
                    </MenuItem>
                    <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
                        Excluir
                    </MenuItem>
                </Menu>

                <DentistModal
                    open={isModalOpen}
                    onClose={closeModal}
                    onSave={saveDentist}
                    dentist={selectedDentist}
                />

                <Dialog
                    open={isConfirmModalOpen}
                    onClose={closeConfirmModal}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold' }}>
                        {"Confirmar Exclusão"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Você tem certeza que deseja excluir este dentista? Essa ação não pode ser desfeita.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeConfirmModal}>Cancelar</Button>
                        <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
                            Excluir
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}

export default DentistList;